import { AlertTriangle, Trash2 } from 'lucide-react';

import { Field } from '@/components/Field';
import { NumberInput } from '@/components/NumberInput';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { formatEur } from '@/lib/format';
import { getTierColor } from '@/lib/tierColors';
import { useSimulatorState } from '@/store/useSimulatorState';

const SHARE_TOLERANCE = 0.01;

export function TiersPanel() {
  const { tiers, addTier, updateTier, removeTier } = useSimulatorState();

  const shareSum = tiers.reduce((sum, t) => sum + t.acquisitionShare, 0);
  const shareOk = Math.abs(shareSum - 1) <= SHARE_TOLERANCE;

  const handleAutoAdjust = () => {
    if (shareSum === 0) {
      const equal = 1 / tiers.length;
      tiers.forEach((t) => updateTier(t.id, { acquisitionShare: equal }));
    } else {
      tiers.forEach((t) => updateTier(t.id, { acquisitionShare: t.acquisitionShare / shareSum }));
    }
  };

  return (
    <div className="flex flex-col gap-4 py-2">
      {/* Bandeau somme parts */}
      <div className="flex items-center gap-2">
        <Badge variant={shareOk ? 'default' : 'destructive'}>
          {Math.round(shareSum * 100)} % / 100 %
        </Badge>
        <span className="text-xs text-muted-foreground">Parts d&apos;acquisition</span>
        {!shareOk && (
          <Button variant="outline" size="xs" onClick={handleAutoAdjust} className="ml-auto">
            Auto-ajuster
          </Button>
        )}
      </div>

      {/* Liste des paliers */}
      {tiers.map((tier, index) => {
        const color = getTierColor(index);
        const isChurn100 = tier.monthlyChurnRate >= 1;
        const entryMargin = (tier.entryPrice ?? 0) - (tier.entryCost ?? 0);
        return (
          <Card key={tier.id}>
            <CardHeader className="flex flex-row items-center gap-2 pb-2 pt-3">
              <div
                className="size-3 shrink-0 rounded-full"
                style={{ backgroundColor: color }}
                aria-hidden
              />
              <Input
                defaultValue={tier.name}
                className="h-7 flex-1 border-none p-0 text-sm font-semibold shadow-none focus-visible:ring-0"
                onBlur={(e) => updateTier(tier.id, { name: e.target.value })}
                aria-label="Nom du palier"
              />
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Supprimer le palier ${tier.name}`}
                disabled={tiers.length <= 1}
                onClick={() => removeTier(tier.id)}
              >
                <Trash2 />
              </Button>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 pb-3">
              <div className="grid grid-cols-3 gap-3">
                <Field id={`tier-price-${tier.id}`} label="Prix / mois (€)">
                  <NumberInput
                    id={`tier-price-${tier.id}`}
                    value={tier.monthlyPrice}
                    onChange={(v) => updateTier(tier.id, { monthlyPrice: v })}
                    min={0}
                    decimals={2}
                  />
                </Field>
                <Field id={`tier-churn-${tier.id}`} label="Churn (%)">
                  <NumberInput
                    id={`tier-churn-${tier.id}`}
                    value={tier.monthlyChurnRate * 100}
                    onChange={(v) => updateTier(tier.id, { monthlyChurnRate: v / 100 })}
                    min={0}
                    max={100}
                    decimals={1}
                  />
                </Field>
                <Field id={`tier-share-${tier.id}`} label="Part acq. (%)">
                  <NumberInput
                    id={`tier-share-${tier.id}`}
                    value={tier.acquisitionShare * 100}
                    onChange={(v) => updateTier(tier.id, { acquisitionShare: v / 100 })}
                    min={0}
                    max={100}
                    decimals={1}
                  />
                </Field>
              </div>

              <Field
                id={`tier-support-cost-${tier.id}`}
                label="Coût support / client / mois (€)"
                tooltip="Coût mensuel de support et d'exploitation par client actif du palier. Intégré au coût mensuel récurrent."
              >
                <NumberInput
                  id={`tier-support-cost-${tier.id}`}
                  value={tier.costPerClientPerMonth ?? 0}
                  onChange={(v) => updateTier(tier.id, { costPerClientPerMonth: v })}
                  min={0}
                  decimals={2}
                />
              </Field>

              {/* Frais d'entrée ponctuels (encaissés une fois à la souscription) */}
              <div className="flex flex-col gap-2 rounded-md border border-dashed p-2.5">
                <span className="text-xs font-medium text-muted-foreground">
                  Frais d&apos;entrée (ponctuel, à la souscription)
                </span>
                <div className="grid grid-cols-2 gap-3">
                  <Field id={`tier-entry-price-${tier.id}`} label="Prix d'entrée (€)">
                    <NumberInput
                      id={`tier-entry-price-${tier.id}`}
                      value={tier.entryPrice ?? 0}
                      onChange={(v) => updateTier(tier.id, { entryPrice: v })}
                      min={0}
                      decimals={2}
                    />
                  </Field>
                  <Field id={`tier-entry-cost-${tier.id}`} label="Coût unitaire (€)">
                    <NumberInput
                      id={`tier-entry-cost-${tier.id}`}
                      value={tier.entryCost ?? 0}
                      onChange={(v) => updateTier(tier.id, { entryCost: v })}
                      min={0}
                      decimals={2}
                    />
                  </Field>
                </div>
                {entryMargin !== 0 && (
                  <span
                    className={`text-xs ${entryMargin < 0 ? 'text-destructive' : 'text-muted-foreground'}`}
                  >
                    Marge nette par vente : {formatEur(entryMargin)}
                  </span>
                )}
              </div>

              {isChurn100 && (
                <div className="flex items-center gap-1.5 text-xs text-destructive">
                  <AlertTriangle className="size-3.5 shrink-0" />
                  Avec un churn de 100 %, aucun client ne sera retenu d&apos;un mois à l&apos;autre.
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <Button
        variant="outline"
        size="sm"
        disabled={tiers.length >= 10}
        onClick={() =>
          addTier({
            name: 'Nouveau palier',
            monthlyPrice: 0,
            monthlyChurnRate: 0.05,
            acquisitionShare: 0,
          })
        }
      >
        + Ajouter un palier
      </Button>
    </div>
  );
}
